/**
 * How a goal and a milestone look on the canvas (route layout, but the cards
 * read the same in any layout). Applied after renderImpactPathNode has drawn
 * the card, so the renderer itself only learns a
 * kind label.
 *
 *   mission (the map's root)           the strongest card: white, scaled up, "LÄHTÖ" above
 *   town (a team's scenario root)      solid teal card with dark bold text
 *   goal (`type 'tavoite'`)            thick gold border, tinted fill, glow, a flag
 *   milestone (`type 'virstanpylvas'`) violet border and tint, a signpost, a status chip from node_analysis.status
 */
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Flag, Milestone } from 'lucide-react';
import { NODE_CARD_WIDTH } from './nodeCardMetrics';
import { GOAL_TYPE, MILESTONE_TYPE } from './layouts/route';

const GOAL_COLOUR = '#f0b35e';
const MILESTONE_COLOUR = '#a78bfa';
const MILESTONE_FILL = '#2a2540'; // the violet over the dark canvas, opaque
const START_COLOUR = '#4cc7ce';
const MISSION_COLOUR = '#ffffff';
const MISSION_SCALE = 1.6;

const ANCHOR_INK = '#0b0d12';

/**
 * Make a card one of the two anchors of its lane — not a regular node: a solid
 * colour card with dark bold text, a glow, and a big label above it.
 */
function anchor(card, rect, colour, label) {
  // Inline styles, not attributes: a later redraw pass resets the card's fill attribute
  rect.attr('stroke', '#ffffff').attr('stroke-width', 3).attr('fill', colour)
    .style('fill', colour).style('stroke', '#ffffff').style('stroke-width', '3px');
  // No glow: an SVG filter is re-rasterised on every zoom frame and made the big map crawl
  card.selectAll('text, tspan')
    .attr('fill', ANCHOR_INK).style('fill', ANCHOR_INK).attr('font-weight', 700);
  // The source icon (nodeSource.js) in the same ink as the text on a solid card
  card.selectAll('.node-source svg').attr('stroke', ANCHOR_INK).style('stroke', ANCHOR_INK);
  if (!label || !card.select('.route-anchor-label').empty()) return;
  const x = Number(rect.attr('x')) || 0;
  const y = Number(rect.attr('y')) || 0;
  card.append('text')
    .attr('class', 'route-anchor-label')
    .attr('x', x).attr('y', y - 12)
    .attr('font-size', 26).attr('font-weight', 800).attr('letter-spacing', '0.08em')
    .attr('fill', colour)
    .attr('pointer-events', 'none')
    .text(label.toUpperCase());
}

export const MILESTONE_STATUS_COLOURS = {
  saavutettu: '#4caf50',
  osittain: '#f0b35e',
  ei: '#e94b6e',
  avoin: '#8a8fa3',
};

/** The card's kind label for a goal or milestone, or null for every other card. */
export function routeKindLabel(node, t) {
  if (node?.type === GOAL_TYPE) return t('impactPath.route.goal');
  if (node?.type === MILESTONE_TYPE) return t('impactPath.route.milestone');
  return null;
}

export function markRouteNode(group, node, t) {
  const isMission = node?.type === 'root' || node?.isRoot === true;
  const isStart = Boolean(node?.is_scenario_starter);
  if (!isMission && !isStart && node?.type !== GOAL_TYPE && node?.type !== MILESTONE_TYPE) return;
  const card = group.select('.tree-node');
  if (card.empty()) return;
  const rect = card.selectAll('rect')
    .filter(function filterCard() {
      return Number(this.getAttribute('width')) === NODE_CARD_WIDTH && this.getAttribute('fill') !== 'none'
        && !this.classList.contains('card-base');
    });
  if (rect.empty()) return;
  const x = Number(rect.attr('x')) || 0;
  const y = Number(rect.attr('y')) || 0;
  const right = x + NODE_CARD_WIDTH;

  if (isMission) {
    // The mission is the one start every lane shares: the biggest, brightest card on the map
    anchor(card, rect, MISSION_COLOUR, t('impactPath.route.start'));
    card.select('.route-anchor-label').attr('fill', START_COLOUR).attr('font-size', 30);
    card.attr('transform', `${card.attr('transform') || ''} scale(${MISSION_SCALE})`.trim());
    return;
  }
  if (isStart) {
    anchor(card, rect, START_COLOUR, null);
    return;
  }
  if (node.type === GOAL_TYPE) {
    anchor(card, rect, GOAL_COLOUR, t('impactPath.route.goal'));
    card.append('g')
      .attr('class', 'route-goal-flag')
      .attr('transform', `translate(${right - 26}, ${y + (Number(rect.attr('height')) || 90) - 24})`)
      .attr('pointer-events', 'none')
      .html(renderToStaticMarkup(createElement(Flag, { size: 20, strokeWidth: 2.4, color: ANCHOR_INK })));
    return;
  }

  // A milestone is a stop on the route: its own colour (none of valence green/red,
  // goal gold or town teal), a thick border, a tint and a signpost.
  // Solid, not a see-through tint: lines behind the card must not show through it
  rect.style('stroke', MILESTONE_COLOUR).style('stroke-width', '3px').style('fill', MILESTONE_FILL);
  if (card.select('.route-milestone-sign').empty()) {
    card.append('g')
      .attr('class', 'route-milestone-sign')
      .attr('transform', `translate(${x + 10}, ${y + (Number(rect.attr('height')) || 90) - 26})`)
      .attr('pointer-events', 'none')
      .html(renderToStaticMarkup(createElement(Milestone, { size: 18, strokeWidth: 2.4, color: MILESTONE_COLOUR })));
  }

  const status = node.node_analysis?.status;
  if (!status || !MILESTONE_STATUS_COLOURS[status] || !card.select('.route-milestone-status').empty()) return;
  const colour = MILESTONE_STATUS_COLOURS[status];
  const label = t(`impactPath.route.status.${status}`);
  const width = Math.max(48, label.length * 6.2 + 16);
  // Bottom right: the top-right corner is where a proposed card's tag goes.
  const bottom = y + (Number(rect.attr('height')) || 90);
  const chip = card.append('g')
    .attr('class', 'route-milestone-status')
    .attr('transform', `translate(${right - width - 8}, ${bottom - 21})`)
    .attr('pointer-events', 'none');
  chip.append('rect')
    .attr('width', width).attr('height', 16).attr('rx', 8)
    .attr('fill', `${colour}2e`).attr('stroke', colour).attr('stroke-width', 1);
  chip.append('text')
    .attr('x', width / 2).attr('y', 11.5)
    .attr('text-anchor', 'middle')
    .attr('font-size', 9).attr('font-weight', 700).attr('letter-spacing', '0.04em')
    .attr('fill', colour)
    .text(label);
}
